import { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import TextareaAutosize from 'react-textarea-autosize';
import type { DocumentUser } from '@shared/schema';

// Import textarea-caret library for precise cursor positioning  
// @ts-ignore - No types available for textarea-caret library
import getCaretCoordinates from 'textarea-caret';

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onMentionSelect?: (user: DocumentUser) => void;
  placeholder?: string;
  className?: string;
  minRows?: number;
  maxRows?: number;
  users: DocumentUser[];
}

interface MentionMatch {
  trigger: string;
  userSelected: DocumentUser | null;
  startIndex: number;
  endIndex: number;
}

export const MentionTextarea = forwardRef<HTMLTextAreaElement, MentionTextareaProps>(function MentionTextarea({ 
  value, 
  onChange, 
  users, 
  onKeyDown,
  onMentionSelect,
  placeholder,
  className,
  minRows,
  maxRows,
  ...textareaProps 
}: MentionTextareaProps, forwardedRef: React.ForwardedRef<HTMLTextAreaElement>) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionStart, setMentionStart] = useState(0);
  
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mentionsDropdownRef = useRef<HTMLDivElement | null>(null);
  
  // Combined ref callback that updates both internal and forwarded refs
  const setRef = useCallback((element: HTMLTextAreaElement | null) => {
    textareaRef.current = element;
    
    if (forwardedRef) {
      if (typeof forwardedRef === 'function') {
        forwardedRef(element);
      } else {
        forwardedRef.current = element;
      }
    }
  }, [forwardedRef]);
  
  // Filter users based on mention query
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(mentionQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(mentionQuery.toLowerCase())
  );
  


  // Detect @ mentions in the text
  const detectMention = (text: string, cursorPosition: number) => {
    const beforeCursor = text.slice(0, cursorPosition);
    const match = beforeCursor.match(/@(\w*)$/);
    
    if (match) {
      const query = match[1];
      const start = cursorPosition - match[0].length;
      return { query, start, found: true };
    }
    
    return { query: '', start: 0, found: false };
  };

  // Calculate position for mention popup using industry-standard textarea-caret library
  const calculateMentionPosition = (start: number) => {
    if (!textareaRef.current) return { top: 100, left: 100 };
    
    try {
      // Use textarea-caret library for pixel-perfect positioning (GitHub/Twitter standard)
      const coords = getCaretCoordinates(textareaRef.current, start);
      const rect = textareaRef.current.getBoundingClientRect();
      
      return {
        top: rect.top + coords.top + coords.height + window.scrollY,
        left: rect.left + coords.left + window.scrollX
      };
    } catch (error) {
      console.warn('Failed to calculate caret position:', error);
      // Fallback to simple positioning
      const rect = textareaRef.current.getBoundingClientRect();
      return {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      };
    }
  };

  const handleInputChange = (newValue: string) => {
    onChange(newValue);
    
    if (!textareaRef.current) return;
    
    const cursorPosition = textareaRef.current.selectionStart || 0;
    const mention = detectMention(newValue, cursorPosition);
    
    if (mention.found) {
      setMentionQuery(mention.query);
      setMentionStart(mention.start);
      setSelectedIndex(0);
      const position = calculateMentionPosition(mention.start);
      setMentionPosition(position);
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (user: DocumentUser) => {
    if (!textareaRef.current) return;
    
    const beforeMention = value.slice(0, mentionStart);
    const afterMention = value.slice(textareaRef.current.selectionStart || 0);
    const newValue = `${beforeMention}@${user.name} ${afterMention}`;
    
    onChange(newValue);
    onMentionSelect?.(user);
    setShowMentions(false);
    
    // Set cursor position after mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newPosition = mentionStart + user.name.length + 2; // +2 for @ and space
        textareaRef.current.setSelectionRange(newPosition, newPosition);
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showMentions && filteredUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredUsers.length - 1));
        return;
      }
      
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        return;
      }
      
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        insertMention(filteredUsers[selectedIndex]);
        return;
      }
      
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
    }
    
    // Call the original onKeyDown if provided
    onKeyDown?.(e);
  };


  // Close mentions when clicking or tapping outside
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      // Only handle primary pointer with left button
      if (!event.isPrimary || event.button !== 0) {
        return;
      }
      
      const target = event.target as Node;
      
      // Check if pointer down is outside both textarea and mentions dropdown
      const outsideTextarea = !textareaRef.current || !textareaRef.current.contains(target);
      const outsideDropdown = !mentionsDropdownRef.current || !mentionsDropdownRef.current.contains(target);
      
      if (outsideTextarea && outsideDropdown) {
        setShowMentions(false);
      }
    };
    
    if (showMentions) {
      document.addEventListener('pointerdown', handlePointerDown);
      return () => document.removeEventListener('pointerdown', handlePointerDown);
    }
  }, [showMentions]);



  return (
    <div className="relative">
      <TextareaAutosize
        ref={setRef}
        value={value}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        minRows={minRows}
        maxRows={maxRows}
        {...textareaProps}
      />
      
      {showMentions && filteredUsers.length > 0 && createPortal(
        <div 
          ref={mentionsDropdownRef}
          className="fixed z-[9999] w-64 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-md shadow-xl max-h-64 overflow-y-auto"
          style={{
            top: mentionPosition.top,
            left: mentionPosition.left,
          }}
        >
          {filteredUsers.slice(0, 10).map((user, index) => (
            <div
              key={user.id}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                index === selectedIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
              }`}
              onClick={() => insertMention(user)}
            >
              <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{user.name}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
});